import { useCallback, useEffect, useState } from "react";
import { motion } from "motion/react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { adminStyles } from "./adminStyles";
import ImageUploadField from "./ImageUploadField";

export default function AdminPuppyFormPage({
                                               primaryColor = "#8D6E63",
                                               secondaryColor = "#A1887F",
                                           }) {
    const navigate = useNavigate();
    const { id, puppyId } = useParams();

    const isEditing = Boolean(puppyId);

    const [litter, setLitter] = useState(null);

    const [name, setName] = useState("");
    const [gender, setGender] = useState("Hündin");
    const [color, setColor] = useState("");
    const [character, setCharacter] = useState("");
    const [status, setStatus] = useState("");
    const [image, setImage] = useState("");
    const [sortOrder, setSortOrder] = useState(0);

    const [isLoading, setIsLoading] = useState(isEditing);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const loadLitter = useCallback(async () => {
        if (!id) return;

        const { data, error } = await supabase
            .from("litters")
            .select("id, name")
            .eq("id", id)
            .single();

        if (error) {
            console.error("Fehler beim Laden des Wurfs:", error);
            return;
        }

        setLitter(data);
    }, [id]);

    const loadPuppy = useCallback(async () => {
        if (!puppyId) return;

        setIsLoading(true);
        setErrorMessage("");

        const { data, error } = await supabase
            .from("puppies")
            .select("*")
            .eq("id", puppyId)
            .single();

        if (error) {
            console.error("Fehler beim Laden des Welpen:", error);
            setErrorMessage("Der Welpe konnte nicht geladen werden.");
            setIsLoading(false);
            return;
        }

        setName(data.name || "");
        setGender(data.gender || "Hündin");
        setColor(data.color || "");
        setCharacter(data.character || "");
        setStatus(data.status || "");
        setImage(data.image || "");
        setSortOrder(data.sort_order ?? 0);

        setIsLoading(false);
    }, [puppyId]);

    useEffect(() => {
        loadLitter();

        if (isEditing) {
            loadPuppy();
        } else {
            setIsLoading(false);
        }
    }, [isEditing, loadLitter, loadPuppy]);

    async function handleSubmit(event) {
        event.preventDefault();

        if (!name.trim()) {
            setErrorMessage("Bitte gib einen Namen ein.");
            return;
        }

        setIsSaving(true);
        setErrorMessage("");

        const payload = {
            litter_id: id,
            name: name.trim(),
            gender: gender || null,
            color: color.trim() || null,
            character: character.trim() || null,
            status: status.trim() || null,
            image: image.trim() || null,
            sort_order: Number(sortOrder) || 0,
            updated_at: new Date().toISOString(),
        };

        if (isEditing) {
            const { error } = await supabase
                .from("puppies")
                .update(payload)
                .eq("id", puppyId);

            setIsSaving(false);

            if (error) {
                console.error("Fehler beim Speichern:", error);
                setErrorMessage("Der Welpe konnte nicht gespeichert werden.");
                return;
            }

            navigate(`/admin/litters/${id}/puppies`);
            return;
        }

        const { error } = await supabase.from("puppies").insert([
            {
                ...payload,
                created_at: new Date().toISOString(),
            },
        ]);

        setIsSaving(false);

        if (error) {
            console.error("Fehler beim Erstellen:", error);
            setErrorMessage("Der Welpe konnte nicht erstellt werden.");
            return;
        }

        navigate(`/admin/litters/${id}/puppies`);
    }

    if (isLoading) {
        return (
            <main style={adminStyles.dashboardPage}>
                <div style={adminStyles.dashboardContainer}>
                    <p style={{ color: "#666" }}>Welpe wird geladen...</p>
                </div>
            </main>
        );
    }

    return (
        <main style={adminStyles.dashboardPage}>
            <div style={adminStyles.dashboardContainer}>
                <header style={adminStyles.dashboardHeader}>
                    <div>
                        <h1 style={{ ...adminStyles.title, color: primaryColor }}>
                            {isEditing ? "Welpe bearbeiten" : "Neuer Welpe"}
                        </h1>
                        <p style={adminStyles.subtitle}>
                            {litter?.name
                                ? `Welpe für ${litter.name} verwalten.`
                                : "Welpe verwalten."}
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => navigate(`/admin/litters/${id}/puppies`)}
                        style={{
                            ...adminStyles.secondaryButton,
                            backgroundColor: secondaryColor,
                        }}
                    >
                        Zurück
                    </button>
                </header>

                <motion.form
                    onSubmit={handleSubmit}
                    style={{
                        ...adminStyles.dashboardCard,
                        maxWidth: "850px",
                    }}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    {errorMessage && <div style={adminStyles.error}>{errorMessage}</div>}

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns:
                                "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
                            gap: "1rem",
                        }}
                    >
                        <div style={adminStyles.field}>
                            <label style={adminStyles.label} htmlFor="name">
                                Name
                            </label>
                            <input
                                id="name"
                                type="text"
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                                style={adminStyles.input}
                                placeholder="z. B. Amelie"
                            />
                        </div>

                        <div style={adminStyles.field}>
                            <label style={adminStyles.label} htmlFor="gender">
                                Geschlecht
                            </label>
                            <select
                                id="gender"
                                value={gender}
                                onChange={(event) => setGender(event.target.value)}
                                style={adminStyles.input}
                            >
                                <option value="Hündin">Hündin</option>
                                <option value="Rüde">Rüde</option>
                            </select>
                        </div>

                        <div style={adminStyles.field}>
                            <label style={adminStyles.label} htmlFor="sortOrder">
                                Sortierung
                            </label>
                            <input
                                id="sortOrder"
                                type="number"
                                value={sortOrder}
                                onChange={(event) => setSortOrder(event.target.value)}
                                style={adminStyles.input}
                            />
                        </div>
                    </div>

                    <div style={adminStyles.field}>
                        <label style={adminStyles.label} htmlFor="color">
                            Farbe
                        </label>
                        <input
                            id="color"
                            type="text"
                            value={color}
                            onChange={(event) => setColor(event.target.value)}
                            style={adminStyles.input}
                            placeholder="z. B. chocolate"
                        />
                    </div>

                    <div style={adminStyles.field}>
                        <label style={adminStyles.label} htmlFor="status">
                            Status
                        </label>
                        <input
                            id="status"
                            type="text"
                            value={status}
                            onChange={(event) => setStatus(event.target.value)}
                            style={adminStyles.input}
                            placeholder="z. B. Frei, Vergeben, Bleibt bei uns"
                        />
                    </div>

                    <ImageUploadField
                        label="Welpenbild"
                        value={image}
                        onChange={setImage}
                        folder="puppies"
                        primaryColor={primaryColor}
                        secondaryColor={secondaryColor}
                        previewAlt={name || "Welpenbild"}
                    />

                    <div style={adminStyles.field}>
                        <label style={adminStyles.label} htmlFor="character">
                            Charakter
                        </label>
                        <textarea
                            id="character"
                            value={character}
                            onChange={(event) => setCharacter(event.target.value)}
                            style={{
                                ...adminStyles.input,
                                minHeight: "130px",
                                resize: "vertical",
                                lineHeight: 1.6,
                                fontFamily: "inherit",
                            }}
                            placeholder="z. B. Verspielt und neugierig"
                        />
                    </div>

                    <div
                        style={{
                            display: "flex",
                            gap: "0.75rem",
                            flexWrap: "wrap",
                        }}
                    >
                        <motion.button
                            type="submit"
                            disabled={isSaving}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: primaryColor,
                                opacity: isSaving ? 0.7 : 1,
                            }}
                            whileHover={{
                                scale: isSaving ? 1 : 1.03,
                                backgroundColor: secondaryColor,
                            }}
                            whileTap={{ scale: isSaving ? 1 : 0.97 }}
                        >
                            {isSaving
                                ? "Wird gespeichert..."
                                : isEditing
                                    ? "Änderungen speichern"
                                    : "Welpe erstellen"}
                        </motion.button>

                        <button
                            type="button"
                            onClick={() => navigate(`/admin/litters/${id}/puppies`)}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: "#777",
                            }}
                        >
                            Abbrechen
                        </button>
                    </div>
                </motion.form>
            </div>
        </main>
    );
}